"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import Image from "next/image";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Container } from "@/components";
import { teamMembers } from "@/data/TeamData";
import { TeamMember } from "@/lib/types";
import { cn } from "@/lib/utils/utils";
interface TeamPagerProps {
  currentSlug: string;
}
const cardBase = "group flex min-w-0 items-center gap-4 rounded-2xl border-2 border-black bg-secondary p-4 shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-transform duration-300 hover:-translate-y-1 md:p-5";
const Thumb = t0 => {
  const $ = _c(3);
  const {
    member
  } = t0;
  let t1;
  if ($[0] !== member.image || $[1] !== member.name) {
    t1 = <span className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl border-2 border-black"><Image src={member.image} alt={member.name} fill={true} sizes="56px" className="object-cover" /></span>;
    $[0] = member.image;
    $[1] = member.name;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// Blog-style previous / next navigation across the roster order, so a visitor
// can walk the whole team without bouncing back to the grid each time.
const TeamPager = t0 => {
  const $ = _c(36);
  const {
    currentSlug
  } = t0;
  const n = teamMembers.length;
  const i = teamMembers.findIndex(m => m.slug === currentSlug);
  if (i === -1 || n < 2) {
    return null;
  }
  const prev = teamMembers[(i - 1 + n) % n];
  const next = teamMembers[(i + 1) % n];
  const t1 = `/about-us/${prev.slug}`;
  const t2 = `Previous teammate: ${prev.name}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowLeft className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:-translate-x-1" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== prev) {
    t4 = <Thumb member={prev} />;
    $[1] = prev;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Previous</span>;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== prev.name) {
    t6 = <span className="block truncate font-black uppercase tracking-tight text-lg">{prev.name}</span>;
    $[4] = prev.name;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== prev.designation) {
    t7 = <span className="block truncate text-xs text-muted-foreground">{prev.designation}</span>;
    $[6] = prev.designation;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <span className="min-w-0">{t5}{t6}{t7}</span>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t4 || $[14] !== t8) {
    t9 = <Link href={t1} aria-label={t2} className={cardBase}>{t3}{t4}{t8}</Link>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = `/about-us/${next.slug}`;
  const t11 = `Next teammate: ${next.name}`;
  let t12;
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn(cardBase, "sm:flex-row-reverse sm:text-right");
    t13 = <ArrowRight className="h-5 w-5 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />;
    $[16] = t12;
    $[17] = t13;
  } else {
    t12 = $[16];
    t13 = $[17];
  }
  let t14;
  if ($[18] !== next) {
    t14 = <Thumb member={next} />;
    $[18] = next;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="block text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Next</span>;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== next.name) {
    t16 = <span className="block truncate font-black uppercase tracking-tight text-lg">{next.name}</span>;
    $[21] = next.name;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== next.designation) {
    t17 = <span className="block truncate text-xs text-muted-foreground">{next.designation}</span>;
    $[23] = next.designation;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t16 || $[26] !== t17) {
    t18 = <span className="min-w-0">{t15}{t16}{t17}</span>;
    $[25] = t16;
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t10 || $[29] !== t11 || $[30] !== t14 || $[31] !== t18) {
    t19 = <Link href={t10} aria-label={t11} className={t12}>{t13}{t14}{t18}</Link>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t14;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t19 || $[34] !== t9) {
    t20 = <section className="bg-background pb-14 md:pb-20"><Container className="px-4 lg:px-10"><div className="grid gap-4 sm:grid-cols-2">{t9}{t19}</div></Container></section>;
    $[33] = t19;
    $[34] = t9;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  return t20;
};
export default TeamPager;